"use client";

import { useCallback, useEffect, useState } from "react";
import HomepageDriverRegistrationForm from "./HomepageDriverRegistrationForm";
import HomepageDriverLiveRadar from "./HomepageDriverLiveRadar";

type Registration = {
  id: number;
  category: string;
  vehicleType: string;
  vehicleModel: string;
  vehicleNumber: string;
  status: string;
  rejectionReason: string | null;
};

type Props = {
  loggedIn: boolean;
  onClose: () => void;
  map: import("leaflet").Map | null;
  activeBooking?: boolean;
  compactOnline?: boolean;
};

export default function HomepageDriverPanel({ loggedIn, onClose, map, activeBooking = false, compactOnline = false }: Props) {
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingRejected, setEditingRejected] = useState(false);

  const loadRegistration = useCallback(async (signal?: AbortSignal) => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/driver-registration", {
        cache: "no-store",
        signal,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to load driver registration.");
      }

      if (!signal?.aborted) {
        setRegistration(data.registration || null);
        setEditingRejected(false);
      }
    } catch (cause) {
      if (!signal?.aborted) {
        setError(
          cause instanceof Error ? cause.message : "Unable to load registration."
        );
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [loggedIn]);

  useEffect(() => {
    const controller = new AbortController();
    void loadRegistration(controller.signal);
    return () => controller.abort();
  }, [loadRegistration]);

  return (
    <section className={`col-span-2 w-full min-w-0 overflow-y-auto sm:col-span-4 ${compactOnline ? "max-h-[44dvh] space-y-2 rounded-xl bg-transparent p-0" : "max-h-[65dvh] space-y-4 rounded-2xl bg-white p-3"}`}>
      <div className={`${compactOnline ? "hidden" : "flex items-center justify-between gap-3"}`}>
        <div>
          <h2 className="text-lg font-bold text-slate-900">DRIVER 🚗🚚</h2>
          <p className="text-xs text-slate-500">
            {activeBooking ? "Active ride in progress" : "Live Radar"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={activeBooking}
          className={`rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold ${activeBooking ? "hidden" : ""}`}
        >
          Close
        </button>
      </div>

      {!loggedIn ? (
        <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          Please login to register as a driver.
        </p>
      ) : loading ? (
        <p className="text-sm text-slate-600">Loading driver details...</p>
      ) : error ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => void loadRegistration()}
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white"
          >
            Retry
          </button>
        </div>
      ) : !registration ? (
        <HomepageDriverRegistrationForm
          onSubmitted={() => void loadRegistration()}
        />
      ) : (
        <div className="space-y-3">
          <div className={`rounded-xl border border-slate-200 bg-slate-50 p-3 ${activeBooking || compactOnline ? "hidden" : ""}`}>
            <p className="text-sm font-bold text-slate-900">
              {registration.vehicleType} · {registration.vehicleModel} · {registration.vehicleNumber}
            </p>
            
          </div>

          {registration.status === "Pending" ? (
            <p className="rounded-xl bg-amber-50 p-3 text-sm font-semibold text-amber-900">
              ⏳ Verification Pending — Admin approval ka wait karein.
            </p>
          ) : registration.status === "Rejected" ? (
            <div className="space-y-2 rounded-xl bg-red-50 p-3 text-sm text-red-900">
              <p className="font-bold">❌ Registration Rejected</p>
              <p>
                Reason: {registration.rejectionReason || "Contact support."}
              </p>
              {!editingRejected ? (
                <button
                  type="button"
                  onClick={() => setEditingRejected(true)}
                  className="rounded-xl bg-red-700 px-4 py-2 font-bold text-white"
                >
                  Edit &amp; Resubmit
                </button>
              ) : (
                <div className="space-y-3 rounded-xl bg-white p-3">
                  <HomepageDriverRegistrationForm
                    existingRegistration={registration}
                    onSubmitted={() => void loadRegistration()}
                  />
                  <button
                    type="button"
                    onClick={() => setEditingRejected(false)}
                    className="rounded-lg border border-slate-300 px-3 py-2 font-semibold text-slate-700"
                  >
                    Cancel Editing
                  </button>
                </div>
              )}
            </div>
          ) : registration.status === "Approved" ? (
            <div className="space-y-2 rounded-xl bg-emerald-50 p-3">
              

                {registration.category === "Passenger" && (
                  <>
<HomepageDriverLiveRadar map={map}
                    vehicleType={registration.vehicleType}
                    vehicleModel={registration.vehicleModel}
                    vehicleNumber={registration.vehicleNumber}
                  />
                    <a
                      href="/ride-requests/scheduled"
                      className="mt-3 flex w-full items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-white px-4 py-3 text-sm font-bold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50"
                    >
                      <span>📅 View Scheduled Ride Requests</span>
                      <span aria-hidden="true">→</span>
                    </a>
                  </>

                )}
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              Registration status: {registration.status}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
